import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { App } from "antd";
import { useCallback } from "react";
import { Menu } from "@devowl-wp/react-folder-tree";
import { useAppTreeControllerRef } from "../../../contexts/appTreeControllerContext.js";
import { isDarkMode } from "../../../utils/darkMode.js";
import { __, _i } from "../../../utils/i18n.js";
import { rmlOpts } from "../../../utils/rmlOpts.js";
import { textDots } from "../../../utils/textDots.js";
function applyFolderChildrenOrder(orderKey, children, setChildren) {
    if (process.env.PLUGIN_CTX !== "pro") {
        return;
    }
/* Silence is golden... Until you have PRO Version! */ }
const asSortableMap = (sortables)=>{
    if (!sortables || typeof sortables !== "object" || Array.isArray(sortables)) {
        return undefined;
    }
    return sortables;
};
const createSortableItems = (sortables, select, selectText, keyPrefix)=>Object.keys(sortables).map((sortKey)=>({
            key: keyPrefix ? `${keyPrefix}:${sortKey}` : sortKey,
            label: /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    sortables[sortKey],
                    " ",
                    select === sortKey && /*#__PURE__*/ _jsxs("strong", {
                        children: [
                            "(",
                            selectText,
                            ")"
                        ]
                    })
                ]
            })
        }));
function buildSortMenuItems(controller) {
    const { store } = controller.props;
    const { selectedId, selected } = store;
    const sortables = asSortableMap(store.treeSortables);
    const isFolder = typeof selectedId === "number" && selectedId > 0;
    if (!isFolder || !selected) {
        return [];
    }
    const subfolderChildren = [];
    if (selected.lastSubOrderBy) {
        subfolderChildren.push({
            key: "reset",
            label: __("Reset order")
        });
    }
    if (!selected.subOrderAutomatically) {
        subfolderChildren.push({
            key: "applyOnce",
            label: __("Apply order once..."),
            children: sortables ? createSortableItems(sortables, selected.lastSubOrderBy, __("Last"), "applyOnce") : undefined
        });
    } else {
        subfolderChildren.push({
            key: "resetAutomatically",
            label: __("Deactivate automatic ordering")
        });
    }
    subfolderChildren.push({
        key: "applyAutomatically",
        label: __("Apply automatic order..."),
        children: sortables ? createSortableItems(sortables, selected.subOrderAutomatically && selected.lastSubOrderBy, __("Latest"), "applyAutomatically") : undefined
    });
    if (selected.lastSubOrderBy) {
        subfolderChildren.push({
            key: "applyReindex",
            label: __("Reindex order")
        });
    }
    return [
        {
            key: "manual",
            label: _i(__("Rearrange {{strong}}%s{{/strong}} placement manually", textDots(selected.title)), {
                strong: /*#__PURE__*/ _jsx("strong", {})
            })
        },
        {
            type: "divider"
        },
        {
            key: "subfolders",
            label: `${__("Subfolders")} (${textDots(selected.title)})`,
            children: subfolderChildren
        }
    ];
}
function useRenderSortMenu() {
    const { message } = App.useApp();
    const controllerRef = useAppTreeControllerRef();
    const handleClick = useCallback(async ({ key, keyPath })=>{
        const controller = controllerRef.current;
        const path = [
            ...keyPath
        ].reverse();
        const orderKey = key.includes(":") ? key.split(":").pop() : key;
        const { selectedId, selected } = controller.props.store;
        if (!selected) {
            return;
        }
        const applyOrder = async (folder, sortKey, automatically)=>{
            if (!rmlOpts.others.isPro && rmlOpts.others.showProHints) {
                controller.setState({
                    showProFeature: "order-subfolders"
                });
            } else {
                const hide = message.loading(_i(__("Reorder subfolders of {{strong}}%s{{/strong}}...", folder.title), {
                    strong: /*#__PURE__*/ _jsx("strong", {})
                }));
                await folder.applyChildrenOrder(sortKey, automatically);
                hide();
            }
        };
        if (key === "manual") {
            controller.setState({
                rearrangeBoxId: selectedId
            });
        } else if (key.startsWith("applyOnce:") || path.includes("applyOnce")) {
            await applyOrder(selected, orderKey);
        } else if (key.startsWith("applyAutomatically:") || path.includes("applyAutomatically")) {
            await applyOrder(selected, orderKey, true);
        } else if (key === "reset") {
            await applyOrder(selected, "original");
        } else if (key === "resetAutomatically") {
            await applyOrder(selected, "deactivate");
        } else if (key === "applyReindex") {
            await applyOrder(selected, "reindex");
        }
    }, [
        controllerRef,
        message
    ]);
    return useCallback(()=>{
        const items = buildSortMenuItems(controllerRef.current);
        return /*#__PURE__*/ _jsx(Menu, {
            items: items.length ? items : undefined,
            onClick: handleClick,
            style: items.length ? undefined : {
                visibility: "hidden"
            },
            theme: isDarkMode() ? "dark" : "light"
        });
    }, [
        controllerRef,
        handleClick
    ]);
}
export { applyFolderChildrenOrder, useRenderSortMenu };
